import { formatCurrency } from "@angular/common";
import { Component, Input, LOCALE_ID, OnInit, ViewChild, inject } from "@angular/core";
import { ChartConfigValue, ChartJsContainerComponent } from "@tylertech/chart-js";
import { percentScale } from "@tylertech/common-components-module";
import { ChartConfiguration, ChartDataset, Tick, TooltipItem } from "chart.js";
import { IAccountModel } from "../../interfaces/iAccountModel";
import { ChartService } from "@tylertech/charting-module";

@Component({
    selector: "account-horizontal-bar-chart",
    templateUrl: "./accountHorizontalBarChart.tmpl.html",
    styleUrls: [
        "./accountHorizontalBarChart.component.scss"
    ],
    standalone: false
})
export class AccountHorizontalBarChartComponent implements OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartService = inject(ChartService);

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    @Input() account: IAccountModel;
    @Input() chartId: string;

    chartConfig: ChartConfiguration<"bar">;

    @Input() overBudget: boolean;
    @Input() pendingLabel: string;

    ngOnInit(): void
    {
        if (!this.pendingLabel)
        {
            this.pendingLabel = "Pending";
        }

        this.displayChart();
    }

    private calculatePercentages(): void
    {
        const total = this.getSumOfDetails();
        for (const name of ["Activity", "Encumbrance", "Pending", "Reserves", "BudgetAvailable"])
        {
            this.account.Details[name + "Percent"] = (this.account.Details[name] / total) * 100;
        }
    }

    private getSumOfDetails(): number
    {
        const activity = this.account.Details.Activity || 0;
        const encumbrance = this.account.Details.Encumbrance || 0;
        const pending = this.account.Details.Pending || 0;
        const reserves = this.account.Details.Reserves || 0;
        const available = this.account.Details.BudgetAvailable || 0;

        let percentSpread = activity + encumbrance + pending + reserves;
        if (!this.overBudget)
        {
            percentSpread += available;
        }

        if (percentSpread === 0)
        {
            percentSpread = Math.max(activity, 0) + Math.max(encumbrance, 0) + Math.max(pending, 0)
                + Math.max(reserves, 0) + Math.max(available, 0);
        }
        return percentSpread;
    }

    private displayChart(): void
    {
        const chartColors = this.$chartService.getColors(6);
        this.calculatePercentages();
        const datasets: Array<ChartDataset<"bar">> = [
            {
                data: [
                    this.account.Details.ActivityPercent || 0,
                    this.account.Details.Activity
                ],
                label: "Activity",
                backgroundColor: chartColors[0],
                hoverBackgroundColor: chartColors[0],
                borderColor: chartColors[0],
            }
        ];

        if (this.account.Details.Encumbrance)
        {
            datasets.push(
                {
                    data: [
                        this.account.Details.EncumbrancePercent || 0,
                        this.account.Details.Encumbrance
                    ],
                    label: "Encumbrances",
                    backgroundColor: chartColors[1],
                    hoverBackgroundColor: chartColors[1],
                    borderColor: chartColors[1],            
                });
        }

        if (this.account.Details.Reserves)
        {
            datasets.push(
                {
                    data: [
                        this.account.Details.ReservesPercent || 0,
                        this.account.Details.Reserves
                    ],
                    label: "Reserves",
                    backgroundColor: chartColors[2],
                    hoverBackgroundColor: chartColors[2],
                    borderColor: chartColors[2],
                });
        }

        datasets.push(
            {
                data: [
                    this.account.Details.PendingPercent || 0,
                    this.account.Details.Pending
                ],
                label: this.pendingLabel || "Pending",
                backgroundColor: chartColors[3],
                hoverBackgroundColor: chartColors[3],
                borderColor: chartColors[3],
            },
            {
                data: [
                    this.overBudget
                        ? 0
                        : this.account.Details.BudgetAvailablePercent || 0,
                    this.account.Details.BudgetAvailable
                ],
                label: "Budget Available",
                backgroundColor: chartColors[4],
                hoverBackgroundColor: chartColors[4],
                borderColor: chartColors[4],
            });

        this.chartConfig = {
            type: "bar",
            data: {
                labels: [""],
                datasets: datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y",
                interaction: {
                    mode: 'nearest',
                    intersect: true,
                },
                scales: {
                    x: {
                        stacked: true,
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        ticks: {
                            maxTicksLimit: 5,
                            color: this.overBudget ? chartColors[5] : undefined,
                            stepSize: 25,
                            callback: (value: number | string, _index: number, _ticks: Tick[]): ChartConfigValue<string> | ChartConfigValue<number> | null | undefined =>
                            {
                                return percentScale(value);
                            },
                        },
                    },
                    y: { stacked: true },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                    },
                    tooltip: {
                        callbacks: {
                            title: (items: TooltipItem<"bar">[]): string | string[] =>
                            {
                                const value = items[0].raw as string | number;
                                // return ChartingUtilities.formatPercent("", value);
                                const valueString = (typeof value === "number"
                                    ? value.toFixed(2)
                                    : value)
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "%";

                                return valueString;
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const label = tooltipItem.label;
                                const value = Number(tooltipItem.raw);
                                return `${label}: ${formatCurrency(value, this.locale, "$")}`;
                            },
                        }
                    },
                },
            },
        };
    }
}
